/* eslint-disable max-len */
import React, {useState, useEffect} from 'react';
import styled, {css} from 'styled-components';
import ScrollContainer from 'react-indiana-drag-scroll';
import {useTranslation} from 'react-i18next';
import {scrollTo} from 'seamless-scroll-polyfill';

import {
    Flex,
    breakpoints,
    mobileMediaQuery,
    notWebMediaQuery,
    tabletMediaQuery,
    webMediaQuery,
    useIsMobile,
} from '../../styles';

import {ReactComponent as ArrowIcon} from './../../assets/arrow.svg';

import GamesGrid from '../GamesGrid';

function Carousel({
    children,
    cellSpacing,
    items,
    isGames,
    featured,
    hideControls,
    indicators,
    snapSlides,
    startIndex,
    infinite,
    horizontalScroll,
    handleClickContent,
    ...props
}) {
    const scrollContainerRef = React.useRef();
    const isMobile = useIsMobile();
    const {t} = useTranslation();

    const [currentSlide, setCurrentSlide] = useState(0);
    //const [visibleSlides, setVisibleSlides] = useState(4);

    const screenWidth = window.innerWidth > 1440 ? 1440 : window.innerWidth;
    const margin = isMobile ? 50 : 80;
    const gutter = isMobile ? 8 : 16;
    let columnQuantity = 16;
    let itemColumns = 4;
    if (window.innerWidth > 768) {
        columnQuantity = 6;
        itemColumns = 4;
    } else if (window.innerWidth > 600) {
        // 12, 5
        columnQuantity = isGames ? 6 : 12;
        itemColumns = isGames ? 5 : 5;
    } else {
        // 6, 6
        columnQuantity = isGames ? 6 : 6;
        itemColumns = isGames ? 6 : 6;
    }

    const [columnSize, setColumnSize] = useState(
        (screenWidth - margin - gutter * (columnQuantity - 1)) / columnQuantity,
    );
    const [slideWidth, setSlideWidth] = useState(
        (itemColumns - 1) * gutter + itemColumns * columnSize,
    );
    const [previewAmount, setPreviewAmount] = useState(
        infinite ? Math.floor(window.innerWidth / slideWidth) * 2 : 0,
    );
    const [gridWidth, setGridWidth] = useState(
        (slideWidth + gutter) * (items.length + previewAmount * 2),
    );

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo(0, 0);
        }
    }, []);

    useEffect(() => {
        startIndex >= 0 && handleStartSlide(startIndex);

        handleWindowResize();

        window.addEventListener('resize', handleWindowResize);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
        // eslint-disable-next-line
    }, [infinite, screenWidth]);

    const handleWindowResize = () => {
        if (scrollContainerRef.current) {
            const slide = scrollContainerRef.current.querySelector(
                '#games-grid',
            ).children[0];

            if (!slide) return;

            recalculateCarouselSizes();
        }
    };

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;

        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleContainerScroll);
        }

        return () => {
            scrollContainer &&
                scrollContainer.removeEventListener(
                    'scroll',
                    handleContainerScroll,
                );
        };

        // eslint-disable-next-line
    }, [scrollContainerRef.current, slideWidth, infinite]);

    const recalculateCarouselSizes = () => {
        const newColumnSize =
            (screenWidth - margin - gutter * (columnQuantity - 1)) /
            columnQuantity;

        const newSlideWidth =
            (itemColumns - 1) * gutter + itemColumns * newColumnSize;

        const newPreviewAmount = infinite
            ? Math.floor(window.innerWidth / newSlideWidth) * 2
            : 0;

        const newGridWidth =
            (newSlideWidth + gutter) * (items.length + newPreviewAmount * 2) -
            margin;

        setColumnSize(newColumnSize);
        setSlideWidth(newSlideWidth);
        setPreviewAmount(newPreviewAmount);
        setGridWidth(newGridWidth);
    };

    const handleContainerScroll = event => {
        const scrollContainer = scrollContainerRef.current;

        if (!scrollContainer) return;

        const slideSize = slideWidth + gutter;

        let index = Math.round(scrollContainer.scrollLeft / slideSize);

        setCurrentSlide(index % 3);
    };

    const handleStartSlide = index => {
        const scrollContainer = scrollContainerRef.current;

        if (!scrollContainer) return;

        const slideSize = slideWidth + gutter;

        scrollContainerRef.current.scrollLeft = items.length * slideSize;
    };

    const handlePreviousSlide = () => {
        const scrollContainer = scrollContainerRef.current;

        if (!scrollContainer) return;

        const slideSize = slideWidth + gutter;
        let index = Math.round(scrollContainer.scrollLeft / slideSize);
        index = reposition(index, scrollContainer, slideSize);
        scrollTo(scrollContainer, {
            left: (index - 1) * slideSize,
            behavior: 'smooth',
        });

        props.handleClickArrow && props.handleClickArrow('previous');
    };

    const handleNextSlide = () => {
        const scrollContainer = scrollContainerRef.current;

        if (!scrollContainer) return;
        const slideSize = slideWidth + gutter;
        let index = Math.round(scrollContainer.scrollLeft / slideSize);
        index = reposition(index, scrollContainer, slideSize);
        scrollTo(scrollContainer, {
            left: (index + 1) * slideSize,
            behavior: 'smooth',
        });

        props.handleClickArrow && props.handleClickArrow('next');
    };

    const handleGoToSlide = slideIndex => {
        const scrollContainer = scrollContainerRef.current;

        if (!scrollContainer) return;

        const slideSize = slideWidth + gutter;

        let index = Math.round(scrollContainer.scrollLeft / slideSize);

        index = reposition(index, scrollContainer, slideSize);

        scrollTo(scrollContainer, {
            left: index * slideSize,
            behavior: 'smooth',
        });

        setCurrentSlide(index % 3);
    };

    const reposition = (index, scrollContainer, slideSize) => {
        // make sure index is greater then item.count and less than item.count + length
        if (index < items.length) {
            scrollContainer.scrollLeft += items.length * slideSize;
            return index + items.length;
        } else if (index >= items.length * 2) {
            scrollContainer.scrollLeft -= items.length * slideSize;
            return index - items.length;
        }

        return index;
    };

    const handleEndScroll = () => {
        if (snapSlides) {
            handleGoToSlide(currentSlide);
        }
    };

    return (
        <Container
            className="carousel-container"
            cellSpacing={cellSpacing}
            gridWidth={gridWidth + (snapSlides === 'center' ? slideWidth : 0)}
            snapSlides={snapSlides}
            slideWidth={slideWidth}
            gutter={gutter}
            visibleWidth={
                scrollContainerRef.current &&
                scrollContainerRef.current.getBoundingClientRect().width
            }
            isGames={isGames}
            margin={margin - gutter * 2}
        >
            <ScrollContainer
                horizontal={horizontalScroll}
                innerRef={scrollContainerRef}
                className="scroll-container"
                onEndScroll={handleEndScroll}
            >
                <GamesGrid
                    items={items}
                    featured={featured}
                    onClickItem={handleClickContent}
                    infinite={true}
                    previewAmount={previewAmount}
                    itemWidth={slideWidth}
                    activeSlide={currentSlide}
                />
            </ScrollContainer>

            {indicators && (
                <IndicatorsContainer>
                    {items.map((item, index) => (
                        <SlideIndicator
                            key={`sliderIndicator_${index + 1}`}
                            onClick={() => handleGoToSlide(index)}
                            current={
                                (currentSlide < 0
                                    ? items.length + currentSlide
                                    : currentSlide > items.length - 1
                                    ? currentSlide - items.length
                                    : currentSlide) === index
                            }
                        />
                    ))}
                </IndicatorsContainer>
            )}

            {!hideControls && (
                <>
                    <LeftControls>
                        <ButtonPrev onClick={handlePreviousSlide}>
                            <ArrowIcon title={t('controls.previous')} />
                        </ButtonPrev>
                    </LeftControls>

                    <RightControls>
                        <ButtonNext onClick={handleNextSlide}>
                            <ArrowIcon title={t('controls.next')} />
                        </ButtonNext>
                    </RightControls>
                </>
            )}
        </Container>
    );
}

export default Carousel;

const Container = styled.div`
    position: relative;

    ::-webkit-scrollbar {
        display: none;
    }

    .scroll-container {
        padding: 8px 0;
        margin: 0px;

        ${webMediaQuery} {
            ${({isGames}) =>
                isGames &&
                css`
                    padding: 8px ${props => props.margin / 2}px;
                `}
        }

        ${tabletMediaQuery} {
            margin-left: -40px;
            padding-left: 40px;
        }

        ${mobileMediaQuery} {
            margin-left: -25px;
            padding-left: 25px;
        }
    }

    #videos-grid,
    #games-grid {
        width: max-content;
        min-width: ${props => props.gridWidth || 0}px;
        grid-auto-flow: column;
        margin: 0px;
        padding-right: 16px;

        grid-template-columns: repeat(
            auto-fill,
            calc((100vw - 80px - (16px * 19)) / 20)
        );

        & > * {
            scroll-snap-align: center;
            grid-column: auto / span 4;

            :last-child {
                margin-right: 16px;
            }

            ${mobileMediaQuery} {
                :last-child {
                    margin-right: 8px;
                }
            }
        }

        @media screen and (min-width: ${breakpoints.xl}px) {
            grid-template-columns: repeat(
                auto-fill,
                calc((1440px - 80px - (16px * 19)) / 20)
            );
        }

        @media screen and (min-width: ${breakpoints.md}px) and (max-width: ${breakpoints.lg}px) {
            grid-template-columns: repeat(
                auto-fill,
                calc((100vw - 80px - (16px * 15)) / 16)
            );

            & > * {
                grid-column: auto / span 4;
            }
        }

        @media screen and (min-width: ${breakpoints.sm}px) and (max-width: ${breakpoints.md}px) {
            grid-template-columns: repeat(
                auto-fill,
                calc((100vw - 80px - (16px * 15)) / 16)
            );

            & > * {
                grid-column: auto / span 5;
            }
        }

        @media screen and (min-width: ${breakpoints.mobile}px) and (max-width: ${breakpoints.sm}px) {
            grid-template-columns: repeat(
                auto-fill,
                calc((100vw - 80px - (16px * 11)) / 12)
            );

            & > * {
                grid-column: auto / span 5;
            }
        }

        ${mobileMediaQuery} {
            grid-template-columns: repeat(
                auto-fill,
                calc((100vw - 50px - (8px * 5)) / 6)
            );

            margin: 0px;

            & > * {
                grid-column: auto / span 6;
            }
        }
    }

    #games-grid {
        width: max-content;
        min-width: ${props => props.gridWidth || 0}px;
        grid-auto-flow: column;
        margin: 0px;

        grid-template-columns: repeat(
            auto-fill,
            calc((100vw - 80px - (16px * 5)) / 6)
        );

        & > * {
            box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.5);
            padding-bottom: calc(9 / 16 * 100%);
            grid-column: auto / span 4;

            :last-child {
                margin-right: 16px;
            }

            ${mobileMediaQuery} {
                :last-child {
                    margin-right: 8px;
                }
            }
        }

        @media screen and (min-width: ${breakpoints.xl}px) {
            grid-template-columns: repeat(
                auto-fill,
                calc((1440px - 80px - (16px * 5)) / 6)
            );
        }

        @media screen and (min-width: ${breakpoints.md}px) and (max-width: ${breakpoints.lg}px) {
            grid-template-columns: repeat(
                auto-fill,
                calc((100vw - 80px - (16px * 5)) / 6)
            );

            & > * {
                grid-column: auto / span 4;
            }
        }

        @media screen and (min-width: ${breakpoints.sm}px) and (max-width: ${breakpoints.md}px) {
            grid-template-columns: repeat(
                auto-fill,
                calc((100vw - 80px - (16px * 5)) / 6)
            );

            & > * {
                grid-column: auto / span 4;
            }
        }

        @media screen and (min-width: ${breakpoints.mobile}px) and (max-width: ${breakpoints.sm}px) {
            grid-template-columns: repeat(
                auto-fill,
                calc((100vw - 80px - (16px * 5)) / 6)
            );

            & > * {
                grid-column: auto / span 5;
            }
        }

        ${mobileMediaQuery} {
            grid-template-columns: repeat(
                auto-fill,
                calc((100vw - 50px - (8px * 5)) / 6)
            );

            margin: 0px;

            & > * {
                grid-column: auto / span 6;
            }
        }

        ${({snapSlides, slideWidth}) =>
            snapSlides === 'center' &&
            css`
                padding-left: ${({visibleWidth, slideWidth, gutter}) =>
                    (visibleWidth - (slideWidth + gutter)) / 2 - gutter}px;
            `}
    }
`;

const LeftControls = styled.div`
    position: absolute;
    top: 50%;
    left: 0px;
    transform: translate(-50%, -50%);
    width: 0px;
    height: 0px;
    overflow: visible;
    z-index: 5;
`;

const RightControls = styled(LeftControls)`
    left: initial;
    right: 0px;
    transform: translate(-50%, -50%);
`;

const ButtonPrev = styled.button`
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 62px;
    height: 62px;
    border: none;
    border-radius: 5px;
    background-color: rgba(255, 255, 255, 0.3);
    box-sizing: border-box;
    cursor: pointer;
    padding: 0px;

    margin: auto;
    left: 52px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-right: 6px;
    svg {
        width: 18px;
        height: 28px;
        transform: scaleX(-1);
        path {
            fill: black;
        }
        margin: auto;
    }

    ${notWebMediaQuery} {
        display: none;
    }

    ${({disabled}) =>
        disabled &&
        css`
            cursor: initial;
            opacity: 0.65;
        `}
`;

const ButtonNext = styled(ButtonPrev)`
    padding-right: initial;
    padding-left: 6px;
    left: -52px;

    svg {
        transform: initial;
    }
`;

const IndicatorsContainer = styled(Flex)`
    max-width: calc(100vw - 40px);
    margin-top: 16px;
`;

const SlideIndicator = styled.div`
    background: rgb(241, 239, 241);
    border-radius: 0px;
    height: 4px;
    width: 16px;
    cursor: pointer;

    :not(:last-child) {
        margin-right: 8px;
    }

    ${props =>
        props.current &&
        css`
            background: linear-gradient(90deg, #f55a86 0%, #ec098c 100%);
        `}
`;
